<template>
  <div class="data-process">
    <div class="process-filters">
      <div class="category-row">
        <el-button v-for="item in categories" :key="item.value" size="small"
                   :class="category === item.value ? 'selected-category' : 'normal-category'"
                   @click="changeCategory(item.value)">{{ item.label }}</el-button>
      </div>
      <div class="condition-row">
        <template v-if="category === 'MULTI_DISPLACEMENT'">
          <span class="condition-label">方向</span>
          <el-select v-model="singleDirection" class="direction-select" size="small" @change="changeDirections">
            <el-option v-for="item in directions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </template>
        <span class="condition-label">测站</span>
        <el-select :key="category === 'SINGLE_DISPLACEMENT' ? 'single' : 'multiple'"
                   v-model="stationSelection" :multiple="category !== 'SINGLE_DISPLACEMENT'"
                   class="station-select" size="small" filterable clearable collapse-tags
                   :loading="stationsLoading" :disabled="stationsLoading" placeholder="请选择测站"
                   :no-data-text="stationEmptyText" @change="changeStations">
          <el-option v-for="station in stationOptions" :key="station.mpcd"
                     :label="stationLabel(station)" :value="station.mpcd" />
        </el-select>
        <el-button v-if="stationError" type="text" size="small" icon="el-icon-refresh" @click="loadStations">重新加载测站</el-button>
        <template v-if="category === 'SINGLE_DISPLACEMENT'">
          <span class="condition-label">方向</span>
          <el-select v-model="selectedDirections" class="direction-select multiple-directions" multiple collapse-tags
                     size="small" placeholder="请选择位移方向" @change="changeDirections">
            <el-option v-for="item in directions" :key="item.value" :label="item.label" :value="item.value"
                       :disabled="!supportedDirections.includes(item.value)" />
          </el-select>
        </template>
        <el-radio-group v-model="period" class="period-switch" size="small" @change="changePeriod">
          <el-radio-button label="MONITOR">监测</el-radio-button>
          <el-radio-button label="DAY">日</el-radio-button>
        </el-radio-group>
      </div>
      <div class="condition-row time-row">
        <span class="condition-label">时间</span>
        <el-date-picker :key="period" v-model="dateRange" size="small" class="time-select"
                        :type="period === 'DAY' ? 'daterange' : 'datetimerange'" range-separator="-"
                        start-placeholder="开始日期" end-placeholder="结束日期" :clearable="false"
                        :value-format="period === 'DAY' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm:ss'"
                        :format="period === 'DAY' ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm'" @change="changeDate" />
        <el-button type="primary" size="small" icon="el-icon-search" :disabled="stationsLoading" @click="queryData(true, true)">查询</el-button>
        <el-button class="export-button" type="primary" size="small" icon="el-icon-download"
                   :loading="exporting" :disabled="stationsLoading || exporting" @click="exportData">导出</el-button>
      </div>
    </div>

    <el-alert v-if="noticeText" class="process-notice" :title="noticeText" :type="noticeType" :closable="false" show-icon />

    <div v-loading="loading" element-loading-text="拼命加载中" element-loading-spinner="el-icon-loading" class="process-content">
      <div class="chart-panel">
        <div class="panel-heading">
          <span>过程线</span>
          <el-tooltip content="恢复图表" placement="top">
            <el-button type="text" icon="el-icon-refresh" aria-label="恢复图表" @click="restoreChart" />
          </el-tooltip>
        </div>
        <div ref="mainChart" class="main-chart" />
        <div v-if="!loading && !hasChartData" class="chart-empty">{{ emptyText }}</div>
      </div>
      <div class="table-panel">
        <el-table :data="records" border size="small" height="100%" :empty-text="emptyText"
                  :header-cell-style="{ textAlign: 'center', background: '#f7f7f7', color: '#323232' }">
          <el-table-column v-for="column in tableColumns" :key="column.property" :prop="column.property"
                           :label="column.label" :min-width="columnWidth(column.property)" :align="columnAlign(column.property)">
            <template slot-scope="scope">{{ displayValue(scope.row[column.property]) }}</template>
          </el-table-column>
        </el-table>
        <div class="pagination-row">
          <el-pagination background :current-page="current" :page-size="size" :page-sizes="[20, 30, 40, 50, 100]"
                         :total="total" layout="total, prev, pager, next, sizes, jumper"
                         @current-change="changePage" @size-change="changeSize" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import processModel from './dataProcessModel';

const {
  CATEGORIES, DIRECTIONS, isDisplacement, stationTypes, normalizeStations, filterStations,
  reconcileSelection, defaultDateRange, validateRequest, normalizeSeriesData, buildChartOption,
  displayValue, downloadFilename
} = processModel;

const BASE_COLUMNS = [
  { property: 'mpcd', label: '测点编号' }, { property: 'mpnm', label: '测点名称' },
  { property: 'indicator', label: '监测指标' }, { property: 'time', label: '测量时间' },
  { property: 'value', label: '监测值' }
];

function errorMessage(error, fallback) {
  return error && error.message ? error.message : fallback;
}

function readBlobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('无法读取导出响应'));
    reader.readAsText(blob, 'utf-8');
  });
}

export default {
  name: 'SafetyDataProcess',
  data() {
    const dateRange = defaultDateRange();
    return {
      categories: CATEGORIES, directions: DIRECTIONS,
      category: 'PZWL', period: 'MONITOR', selectedMpcds: [], selectedDirections: [],
      allStations: [], stationsLoading: false, stationError: '',
      dateRange, monitorClocks: dateRange.map(value => value.slice(11)),
      current: 1, size: 20, total: 0, columns: [], records: [], series: [], warnings: [],
      loading: false, exporting: false, queryError: '', formError: '', hasQueried: false
    };
  },
  computed: {
    stationOptions() { return filterStations(this.allStations, this.category, this.selectedDirections); },
    stationSelection: {
      get() { return this.category === 'SINGLE_DISPLACEMENT' ? this.selectedMpcds[0] || '' : this.selectedMpcds; },
      set(value) { this.selectedMpcds = Array.isArray(value) ? value.slice() : value ? [value] : []; }
    },
    singleDirection: {
      get() { return this.selectedDirections[0] || '0604'; },
      set(value) { this.selectedDirections = value ? [value] : []; }
    },
    supportedDirections() {
      const station = this.allStations.find(item => item.mpcd === this.selectedMpcds[0]);
      return station ? stationTypes(station) : [];
    },
    hasChartData() {
      return this.series.some(item => !['rain', 'temperature'].includes(item.kind) && normalizeSeriesData(item.data).some(point => point[1] !== null));
    },
    tableColumns() { return this.columns.length ? this.columns : BASE_COLUMNS; },
    stationEmptyText() {
      return this.category === 'MULTI_DISPLACEMENT' ? '该方向暂无测站' : '当前水库暂无此类测站';
    },
    emptyText() {
      if (this.stationsLoading) return '正在加载测站';
      if (this.stationError) return '测站加载失败，请重试';
      if (!this.stationOptions.length) return this.stationEmptyText;
      if (!this.selectedMpcds.length) return '请选择测站';
      if (isDisplacement(this.category) && !this.selectedDirections.length) return '请选择位移方向';
      if (this.queryError) return '查询失败，请重试';
      return this.hasQueried ? '所选条件暂无数据' : '请选择条件后查询';
    },
    noticeText() {
      return this.stationError || this.queryError || this.formError ||
        this.warnings.filter(message => !message.includes('暂无')).join('；');
    },
    noticeType() { return this.stationError || this.queryError ? 'error' : 'warning'; }
  },
  created() {
    this._processDisposed = false;
    this._queryVersion = 0;
    this._stationVersion = 0;
    this._exportVersion = 0;
    this._queryCancel = null;
    this._stationCancel = null;
    this._exportCancel = null;
    this._mainChart = null;
    this._sizeObserver = null;
    this.loadStations();
  },
  mounted() {
    window.addEventListener('resize', this.resizeCharts);
    if (typeof ResizeObserver !== 'undefined') {
      this._sizeObserver = new ResizeObserver(this.resizeCharts);
      this._sizeObserver.observe(this.$el);
    }
    this.renderCharts();
  },
  beforeDestroy() {
    this._processDisposed = true;
    this.cancelQuery();
    this.cancelExport();
    this._stationVersion++;
    if (this._stationCancel) this._stationCancel.cancel();
    window.removeEventListener('resize', this.resizeCharts);
    if (this._sizeObserver) this._sizeObserver.disconnect();
    if (this._mainChart) this._mainChart.dispose();
  },
  watch: {
    '$store.state.head.skCd'() { this.loadStations(); },
    '$store.state.head.isCollapse'() { this.$nextTick(this.resizeCharts); }
  },
  methods: {
    displayValue,
    stationLabel(station) { return station.mpnm ? station.mpcd + '（' + station.mpnm + '）' : station.mpcd; },
    columnAlign(property) {
      return ['value', 'temperature', 'waterDepth', 'minValue', 'maxValue'].includes(property) ? 'right' : 'center';
    },
    columnWidth(property) {
      return ['time', 'minTime', 'maxTime'].includes(property) ? 165 : ['mpnm', 'indicator'].includes(property) ? 145 : 115;
    },
    requestBody() {
      const range = Array.isArray(this.dateRange) ? this.dateRange : [];
      return {
        category: this.category, period: this.period, mpcds: this.selectedMpcds.slice(),
        directions: isDisplacement(this.category) ? this.selectedDirections.slice() : [],
        startTime: range[0] || '', endTime: range[1] || '', current: this.current, size: this.size
      };
    },
    cancelQuery() {
      this._queryVersion++;
      if (this._queryCancel) this._queryCancel.cancel();
      this._queryCancel = null;
      this.loading = false;
    },
    cancelExport() {
      this._exportVersion++;
      if (this._exportCancel) this._exportCancel.cancel();
      this._exportCancel = null;
      this.exporting = false;
    },
    clearResults() {
      this.columns = [];
      this.records = [];
      this.series = [];
      this.total = 0;
      this.warnings = [];
      this.queryError = '';
      this.formError = '';
      this.hasQueried = false;
      this.$nextTick(this.renderCharts);
    },
    reconcile(autoSelect) {
      const selection = reconcileSelection(this.allStations, this.category, this.selectedMpcds, this.selectedDirections, autoSelect);
      this.selectedMpcds = selection.mpcds;
      this.selectedDirections = selection.directions;
    },
    async loadStations() {
      this.cancelQuery();
      this.cancelExport();
      const version = ++this._stationVersion;
      if (this._stationCancel) this._stationCancel.cancel();
      const source = axios.CancelToken.source();
      this._stationCancel = source;
      this.stationsLoading = true;
      this.stationError = '';
      this.allStations = [];
      this.selectedMpcds = [];
      this.clearResults();
      try {
        const response = await this.request.get('/rm-bmp/v1/page', {
          params: { current: 1, size: -1 }, cancelToken: source.token
        });
        if (this._processDisposed || version !== this._stationVersion) return;
        if (!response || !response.ok) throw new Error(response && response.error ? response.error.message : '加载测站失败');
        this.allStations = normalizeStations(response.data && response.data.records);
        this.reconcile(true);
        this.stationsLoading = false;
        await this.queryData(true, false);
      } catch (error) {
        if (this._processDisposed || version !== this._stationVersion || axios.isCancel(error)) return;
        this.stationError = errorMessage(error, '加载测站失败，请重试');
      } finally {
        if (!this._processDisposed && version === this._stationVersion) {
          this.stationsLoading = false;
          this._stationCancel = null;
        }
      }
    },
    changeCategory(value) {
      if (this.category === value) return;
      this.category = value;
      this.cancelExport();
      this.reconcile(true);
      this.clearResults();
      this.queryData(true, false);
    },
    changeStations() {
      this.cancelExport();
      this.reconcile(false);
      this.clearResults();
      this.queryData(true, false);
    },
    changeDirections() {
      this.cancelExport();
      // A direction change filters stations for multi-point comparison; single-point keeps the chosen station.
      if (this.category === 'MULTI_DISPLACEMENT') this.reconcile(true);
      this.clearResults();
      this.queryData(true, false);
    },
    changePeriod(value) {
      this.cancelExport();
      const range = Array.isArray(this.dateRange) ? this.dateRange : [];
      if (value === 'DAY') {
        if (range.length === 2) this.monitorClocks = range.map(time => time.slice(11) || '00:00:00');
        this.dateRange = range.map(time => time.slice(0, 10));
      } else {
        this.dateRange = range.map((day, index) => day.slice(0, 10) + ' ' + (this.monitorClocks[index] || '00:00:00'));
      }
      this.clearResults();
      this.queryData(true, false);
    },
    changeDate() {
      this.cancelQuery();
      this.cancelExport();
      this.formError = '';
      if (this.period === 'MONITOR' && Array.isArray(this.dateRange)) {
        this.monitorClocks = this.dateRange.map(time => time.slice(11));
      }
    },
    changePage(value) {
      this.current = value;
      this.queryData(false, true);
    },
    changeSize(value) {
      this.size = value;
      this.queryData(true, true);
    },
    async queryData(resetPage, reportValidation) {
      this.cancelQuery();
      if (resetPage) this.current = 1;
      if (this.stationsLoading || this.stationError) return;
      const body = this.requestBody();
      const validation = validateRequest(body);
      if (validation) {
        this.clearResults();
        if (reportValidation) this.formError = validation;
        return;
      }
      const version = this._queryVersion;
      const source = axios.CancelToken.source();
      this._queryCancel = source;
      this.loading = true;
      this.queryError = '';
      this.formError = '';
      this.warnings = [];
      try {
        const response = await this.request.post('/safety-process/query', body, { cancelToken: source.token });
        if (this._processDisposed || version !== this._queryVersion) return;
        if (!response || !response.ok) throw new Error(response && response.error ? response.error.message : '查询失败');
        const data = response.data || {};
        this.columns = Array.isArray(data.columns) ? data.columns.filter(column => column && column.property) : [];
        this.records = Array.isArray(data.records) ? data.records : [];
        this.series = Array.isArray(data.series) ? data.series : [];
        this.total = Number(data.total) || 0;
        this.warnings = Array.isArray(data.warnings) ? data.warnings.filter(Boolean) : [];
        this.hasQueried = true;
        this.$nextTick(this.renderCharts);
      } catch (error) {
        if (this._processDisposed || version !== this._queryVersion || axios.isCancel(error)) return;
        this.clearResults();
        this.queryError = errorMessage(error, '查询失败，请重试');
      } finally {
        if (!this._processDisposed && version === this._queryVersion) {
          this.loading = false;
          this._queryCancel = null;
        }
      }
    },
    async exportData() {
      const body = this.requestBody();
      const validation = validateRequest(body);
      if (validation) { this.formError = validation; return; }
      this.cancelExport();
      const version = this._exportVersion;
      const source = axios.CancelToken.source();
      this._exportCancel = source;
      this.exporting = true;
      this.formError = '';
      try {
        const response = await this.request.post('/safety-process/export', body, { responseType: 'blob', cancelToken: source.token });
        if (this._processDisposed || version !== this._exportVersion) return;
        const headers = response.headers || {};
        const blob = response.data instanceof Blob ? response.data : new Blob([response.data]);
        const contentType = headers['content-type'] || blob.type || '';
        if (/json|text\//i.test(contentType)) {
          const result = JSON.parse(await readBlobText(blob));
          throw new Error(result && result.error ? result.error.message : '导出失败');
        }
        if (!blob.size) throw new Error('所选条件暂无可导出数据');
        const href = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = href;
        link.download = downloadFilename(headers['content-disposition'], '数据过程-' + body.startTime.slice(0, 10) + '.xlsx');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(href), 1000);
      } catch (error) {
        if (this._processDisposed || version !== this._exportVersion || axios.isCancel(error)) return;
        this.$message.error(errorMessage(error, '导出失败，请重试'));
      } finally {
        if (!this._processDisposed && version === this._exportVersion) {
          this.exporting = false;
          this._exportCancel = null;
        }
      }
    },
    renderCharts() {
      if (this._processDisposed || !this.$refs.mainChart) return;
      if (!this._mainChart) this._mainChart = this.$echarts.init(this.$refs.mainChart);
      this._mainChart.setOption(buildChartOption(this.category, this.series), true);
      this._mainChart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: true });
      this.resizeCharts();
    },
    resizeCharts() {
      if (this._mainChart) this._mainChart.resize();
    },
    restoreChart() {
      if (this._mainChart) {
        this._mainChart.dispatchAction({ type: 'restore' });
        this._mainChart.dispatchAction({ type: 'takeGlobalCursor', key: 'dataZoomSelect', dataZoomSelectActive: true });
      }
    }
  }
};
</script>

<style scoped>
.data-process { flex: 1; width: 0; min-width: 0; height: 100%; min-height: 420px; display: flex; flex-direction: column; box-sizing: border-box; padding: 1rem 2rem 1.5rem; color: #323232; }
.process-filters { flex: none; padding: .5rem 0 1rem; }
.category-row { display: flex; flex-wrap: wrap; gap: 1rem; margin-bottom: 1.5rem; }
.category-row .el-button { margin-left: 0; min-width: 9.2rem; border-radius: .5rem; }
.selected-category { color: #fff; background: #53abfd; border-color: #53abfd; }
.normal-category { color: #323232; background: #f7f7f7; border-color: #f4f4f4; }
.condition-row { display: flex; align-items: center; flex-wrap: wrap; gap: 1rem; margin-bottom: 1rem; }
.condition-label { flex: none; font-size: 1.4rem; }
.station-select { width: 30rem; }
.direction-select { width: 18rem; }
.multiple-directions { width: 26rem; }
.period-switch { margin-left: .5rem; }
.time-row { margin-bottom: 0; }
.time-select { width: 36rem; }
.time-row .el-button + .el-button { margin-left: 0; }
.export-button { margin-left: auto !important; }
.process-notice { flex: none; margin-bottom: 1rem; }
.process-notice ::v-deep .el-alert__title { word-break: break-word; }
.process-content { flex: 1; min-height: 0; display: flex; gap: 2rem; }
.chart-panel, .table-panel { width: calc(50% - 1rem); min-width: 0; background: #fff; position: relative; }
.panel-heading { height: 3.6rem; box-sizing: border-box; padding: 0 1.5rem; display: flex; justify-content: space-between; align-items: center; background: #f7f7f7; font-size: 1.4rem; }
.main-chart { width: 100%; height: calc(100% - 3.6rem); min-height: 160px; }
.chart-empty { position: absolute; left: 0; right: 0; top: 55%; text-align: center; color: #909399; pointer-events: none; font-size: 1.4rem; }
.table-panel { display: flex; flex-direction: column; }
.table-panel > .el-table { flex: 1; min-height: 0; }
.pagination-row { flex: none; overflow-x: auto; padding: 1rem 0 .3rem; text-align: right; }
@media (max-width: 1100px) {
  .data-process { overflow: auto; }
  .process-content { min-height: 480px; gap: 1rem; }
  .station-select { width: 24rem; }
  .chart-panel, .table-panel { width: calc(50% - .5rem); }
}
</style>
